import type { GoogleConnectionStatus } from '../../../shared/types/integrations';
import { apiSlice } from '../../../store/api/apiSlice';

// Types
export interface CoachListItem {
  id: string;
  email: string;
  name: string;
  member_count: number;
  session_count: number;
  artifact_count: number;
  first_login_at?: string;
  last_active_at?: string;
  auto_summary_enabled: boolean;
  auto_reflection_email_enabled?: boolean;
  drive_connected: boolean;
  disabled_at?: string;
}

export type CoachSortBy =
  | 'first_login'
  | 'last_active'
  | 'members'
  | 'sessions'
  | 'artifacts'
  | 'name'
  | 'email';
export type SortDir = 'asc' | 'desc';

export interface ListCoachesResponse {
  coaches: CoachListItem[];
  total: number;
  page: number;
  per: number;
  sort_by: CoachSortBy;
  sort_dir: SortDir;
  include_disabled: boolean;
  exclude_admins: boolean;
  drive_connected?: string;
}

export interface ListCoachesQueryParams {
  q?: string;
  page?: number;
  per?: number;
  sort_by?: CoachSortBy;
  sort_dir?: SortDir;
  include_disabled?: boolean;
  exclude_admins?: boolean;
  drive_connected?: boolean;
}

export interface AdminGoogleConnectionStatus extends GoogleConnectionStatus {
  updated_at?: string;
  last_synced_at?: string;
  name?: string;
  picture_url?: string;
  provider_user_id?: string;
  last_error?: string;
}

export interface CoachDetail {
  id: string;
  email: string;
  name: string;
  org_id?: string;
  org_name?: string;
  lifecycle_status?: string;
  lifecycle_active?: boolean;
  lifecycle_source?: string;
  lifecycle_updated_at?: string;
  inactive_effective_at?: string;
  inactive_reason?: string;
  drive_connected: boolean;
  drive_connected_at?: string;
  auto_summary_enabled: boolean;
  auto_reflection_email_enabled?: boolean;
  auto_summary_enabled_at?: string;
  last_active_at?: string;
  disabled_at?: string;
  disabled_by_person_id?: string;
  google_oauth_status?: AdminGoogleConnectionStatus;
}

export interface CoachMemberItem {
  id: string;
  email: string;
  name: string;
  relationship_type?: string;
  session_count: number;
  artifact_count: number;
  summary_sent_count: number;
  last_session_at?: string;
  last_artifact_at?: string;
}

export interface CoachStats {
  total_members: number;
  active_members: number;
  total_sessions: number;
  total_artifacts: number;
}

export interface GetCoachResponse {
  coach: CoachDetail;
  members: CoachMemberItem[];
  stats: CoachStats;
  skipped_emails?: SummaryEmailItem[];
}

export interface MemberDetail {
  id: string;
  email: string;
  name: string;
  relationship_type?: string;
  relationship_since?: string;
}

export interface ArtifactItem {
  id: string;
  title: string;
  profile_slug: string;
  created_at: string;
  updated_at: string;
  transcript_title?: string;
}

export interface SummaryEmailItem {
  sent_at: string;
  status?: string;
  skip_reason?: string;
  transcript_id?: string;
  email_subject?: string;
  compact_summary?: string;
  member_name?: string;
  transcript_date?: string;
  artifact_id?: string;
  to_email?: string;
}

export interface GetCoachMemberResponse {
  member: MemberDetail;
  session_stats: {
    session_count: number;
    last_session_at?: string;
  };
  artifacts: ArtifactItem[];
  summary_emails?: SummaryEmailItem[];
}

export interface UpdateCoachSettingsRequest {
  auto_summary_enabled?: boolean;
  auto_reflection_email_enabled?: boolean;
}

export interface UpdateCoachSettingsResponse {
  ok: boolean;
  auto_summary_enabled?: boolean;
  auto_reflection_email_enabled?: boolean;
  changed_at: string;
}

export interface UpdateCoachStatusRequest {
  disabled: boolean;
}

export interface UpdateCoachStatusResponse {
  ok: boolean;
  coach_id: string;
  disabled: boolean;
  disabled_at?: string;
  changed_at: string;
}

// API endpoints
export const coachesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // List all coaches with stats
    listCoaches: builder.query<ListCoachesResponse, ListCoachesQueryParams>({
      query: ({
        q,
        page,
        per,
        sort_by,
        sort_dir,
        include_disabled,
        exclude_admins,
        drive_connected,
      }) => ({
        url: '/admin/coaches',
        params: {
          q,
          page,
          per,
          sort_by,
          sort_dir,
          include_disabled,
          exclude_admins,
          drive_connected,
        },
      }),
      providesTags: ['Coaches'],
    }),

    // Get coach detail with members
    getCoach: builder.query<GetCoachResponse, { coachId: string }>({
      query: ({ coachId }) => `/admin/coaches/${coachId}`,
      providesTags: (_result, _error, { coachId }) => [{ type: 'CoachDetail', id: coachId }],
    }),

    // Get member detail with transcripts/artifacts
    getCoachMember: builder.query<GetCoachMemberResponse, { coachId: string; memberId: string }>({
      query: ({ coachId, memberId }) => `/admin/coaches/${coachId}/members/${memberId}`,
      providesTags: (_result, _error, { coachId, memberId }) => [
        { type: 'CoachMember', id: `${coachId}-${memberId}` },
      ],
    }),

    // Update coach settings
    updateCoachSettings: builder.mutation<
      UpdateCoachSettingsResponse,
      { coachId: string; settings: UpdateCoachSettingsRequest }
    >({
      query: ({ coachId, settings }) => ({
        url: `/admin/coaches/${coachId}/settings`,
        method: 'PATCH',
        body: settings,
      }),
      async onQueryStarted({ coachId, settings }, { dispatch, getState, queryFulfilled }) {
        const patches = coachesApi.util
          .selectCachedArgsForQuery(getState(), 'listCoaches')
          .map((queryArgs) =>
            dispatch(
              coachesApi.util.updateQueryData('listCoaches', queryArgs, (draft) => {
                const coach = draft.coaches.find((item) => item.id === coachId);
                if (!coach) return;
                if (settings.auto_summary_enabled !== undefined)
                  coach.auto_summary_enabled = settings.auto_summary_enabled;
                if (settings.auto_reflection_email_enabled !== undefined)
                  coach.auto_reflection_email_enabled = settings.auto_reflection_email_enabled;
              })
            )
          );
        patches.push(
          dispatch(
            coachesApi.util.updateQueryData('getCoach', { coachId }, (draft) => {
              if (settings.auto_summary_enabled !== undefined)
                draft.coach.auto_summary_enabled = settings.auto_summary_enabled;
              if (settings.auto_reflection_email_enabled !== undefined)
                draft.coach.auto_reflection_email_enabled = settings.auto_reflection_email_enabled;
            })
          )
        );
        try {
          await queryFulfilled;
        } catch {
          for (const patch of patches) patch.undo();
        }
      },
      invalidatesTags: (_result, _error, { coachId }) => [
        'Coaches',
        { type: 'CoachDetail', id: coachId },
      ],
    }),

    // Update coach status (enable/disable)
    updateCoachStatus: builder.mutation<
      UpdateCoachStatusResponse,
      { coachId: string; disabled: boolean }
    >({
      query: ({ coachId, disabled }) => ({
        url: `/admin/coaches/${coachId}/status`,
        method: 'PATCH',
        body: { disabled },
      }),
      invalidatesTags: (_result, _error, { coachId }) => [
        'Coaches',
        { type: 'CoachDetail', id: coachId },
      ],
    }),
  }),
});

export const {
  useListCoachesQuery,
  useGetCoachQuery,
  useGetCoachMemberQuery,
  useUpdateCoachSettingsMutation,
  useUpdateCoachStatusMutation,
} = coachesApi;
